import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { useMemo } from 'react';

import filter from 'lodash/filter';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import noop from 'lodash/noop';
import toNumber from 'lodash/toNumber';

import { printErrorToConsole, useSnackBar } from '@sbv-clients/ui-shared';
import { optionsToLabelMap } from '@sbv-clients/util-misc';
import {
  getApiErrorMessage,
  keyToLabel,
  UnwrapArray,
} from '@sbv-clients/util-misc';

import {
  Api,
  EmployeeV2ApiApiV2EmployeesCheckDuplicateClockIdGetRequest,
  EmployeeV2ApiApiV2EmployeesCheckDuplicateIdGetRequest,
  EmployeeV2ApiApiV2EmployeesClockInfoPermissionsPostRequest,
  EmployeeV2ApiApiV2EmployeesCreatePostRequest,
  EmployeeV2ApiApiV2EmployeesEmploymentEmployeeLicensePutRequest,
  EmployeeV2ApiApiV2EmployeesEmploymentGeneralPutRequest,
  EmployeeV2ApiApiV2EmployeesEmploymentHealthPutRequest,
  EmployeeV2ApiApiV2EmployeesEmploymentI9DocumentsPutRequest,
  EmployeeV2ApiApiV2EmployeesNextAvailableIdGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdAccrualGroupPutRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdAccrualGroupsGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdAllLaborLevelsGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdAuditsGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdAuthorizationAuditsGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdAuthorizationDeleteRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdAuthorizationGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdAuthorizationLaborLevelAuthorizationLaborLevelIdClonePostRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdAuthorizationLaborLevelLaborLevelIdDeleteRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdAuthorizationLaborLevelLaborLevelIdPutRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdAuthorizationLaborLevelPostRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdAuthorizationPostRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdAuthorizationPutRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdAuthorizationRolesGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdBaseScheduleGroupPutRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdBaseScheduleGroupsGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdClockInfoAssignmentAllPatchRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdClockInfoAssignmentsPatchRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdClockInfoClockIndexAuditsGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdClockInfoGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdClockInfoLaborLevelsGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdClockInfoListGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdClockInfoLaborLevelsPutRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdClockInfoPostRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdClockInfoPutRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdContactInfoAuditsGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdContactInfoGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdContactInfoPutRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdEffectiveDateConfigurationGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdEmployeeIdPutRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdEmployeeLicenseAuditsGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdEmployeeStatusTypesGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdEmploymentAuditsGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdEmploymentEmployeeInServiceInServiceIdDeleteRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdEmploymentEmployeeInServicesEmployeeInServiceIdAuditsGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdEmploymentEmployeeInServicesGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdEmploymentGeneralGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdEmploymentHealthGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdEmploymentI9DocumentsGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdEmploymentInServiceListServiceCategoryGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdEmploymentInServicePostRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdEmploymentLicenseGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdHeaderGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdHealthAuditsGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdHistoriesGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdHistoriesMinEffectiveDateGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdHistoryEmployeeRecordIdAuditsGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdHistoryPkHistoryIdDeleteRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdHistoryTypesGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdHolidayGroupPutRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdHolidayGroupsGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdJournalsGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdJournalsPkEmployeeJournalIdAuditsGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdJournalsPkEmployeeJournalIdDeleteRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdJournalsPostRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdJournalsPutRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdLaborLevelsCheckPatchRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdLaborLevelsPutRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdMainGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdMiscAuditsGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdMiscGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdMiscPutRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdPayPeriodGroupGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdPayRatePayRateIdAuditsGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdPayRatePayRateIdDeleteRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdPayRatePayRateIdPutRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdPayRatePostRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdPayRatesGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdPayRatesSettingsGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdPayRulePutRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdPayRulesGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdPayTypesGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdProfilePicturePostRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdPutRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdSalariedHoursGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdSalariedHoursPutRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdShiftsAuditsGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdShiftsDeleteRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdShiftsGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdShiftsPostRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdShiftsPutRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdStatusEmployeeStatusIdAuditsGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdStatusesGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdStatusesPkEmployeeStatusIdDeleteRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdStatusesPkEmployeeStatusIdPutRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdStatusesPostRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdWorkingPayPeriodWorkingPayPeriodIdPatchRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdWorkPatternConfigurationGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdWorkPatternsGetRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdWorkPatternsPostRequest,
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdWorkPatternWorkPatternIdAuditsGetRequest,
  EmployeeV2ApiApiV2EmployeesPostRequest,
  EmployeeV2ApiApiV2EmployeesWorkPatternDeleteRequest,
  FacilityApiFacilityIsPhoneNumberUpdateAllowedForIdGetRequest,
  PayTypeEntityResponse,
  useAllLaborLevelsGet,
  useApi,
  UserProfileResponse,
} from '../index';
import {
  EmployeeCacheKeys,
  EntityListCacheKeys,
  TimeCardCacheKeys,
  UserCacheKeys,
} from './util/cache-keys';
import { buildLaborLevelOptions } from './util/transform';

const useEmployeeIdAbsenceRequestsGet = (employeeId: number) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EmployeeAbsenceRequests, { employeeId }],
    queryFn: () =>
      api.employeeApi
        .employeeIdAbsenceRequestsGet({
          id: employeeId,
        })
        .then((response) => response?.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useGetPendingAndRejectedAbsenceRequests = (employeeId: number) => {
  const { data, ...rest } = useEmployeeIdAbsenceRequestsGet(employeeId);

  const { pending = [], rejected = [] } = useMemo(
    () => ({
      pending: filter(data, { absenceRequestStatus: 0 }),
      rejected: filter(data, { absenceRequestStatus: 2 }),
    }),
    [data]
  );
  return {
    pending,
    rejected,
    ...rest,
  };
};

export const useGetAccruals = (employeeId: number) => {
  const api = useApi();

  return useQuery({
    queryKey: [EmployeeCacheKeys.EmployeeAccruals, { employeeId }],
    queryFn: async () => {
      const res = await api.employeeApi.employeeAccrualsGet();
      return res.data;
    },
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useAccountGet = (): UseQueryResult<UserProfileResponse> => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EmployeeAccountProfile],
    queryFn: () =>
      api.accountApi
        .accountMeGet({ isWeb: true })
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

export const useEmployeePermissionsGet = () => {
  const api = useApi();

  return useQuery({
    queryKey: [EmployeeCacheKeys.EmployeePermissions],
    queryFn: () =>
      api.employeeApi
        .employeePermissionsGet({})
        .then((response) => response.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

const isPhoneNumberUpdateAllowed =
  (api: Api) =>
  async (
    body: FacilityApiFacilityIsPhoneNumberUpdateAllowedForIdGetRequest
  ) => {
    return api.facilityApi
      .facilityIsPhoneNumberUpdateAllowedForIdGet(body)
      .then((response) => response.data ?? []);
  };

export const useIsPhoneNumberUpdateAllowed = () => {
  const api = useApi();
  return useMutation(isPhoneNumberUpdateAllowed(api));
};

export const useIsClockPermissionsAllowed = () => {
  const api = useApi();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesClockInfoPermissionsPostRequest) =>
      api.employeeV2Api
        .apiV2EmployeesClockInfoPermissionsPost(body)
        .then((response) => response.data ?? [])
  );
};

export const useEmployeeLaborLevelLevelsGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdAllLaborLevelsGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EmployeeLaborLevelLevels, body],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdAllLaborLevelsGet(body)
        .then((response) => response?.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!body.pkEmployeeId,
  });
};

export const useEmployeeLaborLevelWithOptionsGet = (
  body: Parameters<typeof useEmployeeLaborLevelLevelsGet>[0],
  mappingConfig?: Pick<
    Parameters<typeof buildLaborLevelOptions>[0],
    'getLaborLevelLabel' | 'getLaborLevelLevelLabel'
  >
) => {
  const response = useAllLaborLevelsGet();
  const { data, isLoading, isSuccess } = useEmployeeLaborLevelLevelsGet(body);
  const options = useMemo(
    () =>
      buildLaborLevelOptions({
        levelDefinitions: response.data?.laborLevelsLevelResponse,
        laborLevels: data,
        ...mappingConfig,
      }),
    [data, mappingConfig, response.data?.laborLevelsLevelResponse]
  );
  return { options, isLoading: isLoading || response.isLoading, isSuccess };
};

const getEmployeeHeaderQueryKey = (pkEmployeeId: number) => [
  EmployeeCacheKeys.EmployeeHeader,
  { pkEmployeeId },
];

export const useEmployeeHeaderGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdHeaderGetRequest,
  options?: { acquireLock?: boolean }
) => {
  const api = useApi();
  return useQuery({
    queryKey: getEmployeeHeaderQueryKey(body.pkEmployeeId),
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdHeaderGet(
          body,
          options?.acquireLock ? { params: { acquireLock: true } } : undefined
        )
        .then((response) => response.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: Boolean(body.pkEmployeeId),
  });
};

export type EmployeeHeaderResponse = NonNullable<
  ReturnType<typeof useEmployeeHeaderGet>['data']
>;

export const useUpdateEmployeeHeaderCache = () => {
  const queryClient = useQueryClient();
  return (pkEmployeeId: number, data: EmployeeHeaderResponse) => {
    queryClient.setQueryData(getEmployeeHeaderQueryKey(pkEmployeeId), data);
  };
};

export const useEmployeeDetailsGetWithMutation = () => {
  const api = useApi();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdMainGetRequest) =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdMainGet(body)
        .then((response) => response.data ?? {})
  );
};

export type EmployeeDetailResponse = NonNullable<
  ReturnType<typeof useEmployeeDetailsGetWithMutation>['data']
>;

const useEmployeePayTypesWithOptionsGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdPayTypesGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_PAY_TYPE_WITH_OPTION, body],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdPayTypesGet(body)
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useEmployeeEmploymentAuditsGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdEmploymentAuditsGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_EMPLOYMENT_AUDIT, body],
    queryFn: () =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdEmploymentAuditsGet(body),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(body.pkEmployeeId),
  });
};

export const useEmployeeHistoryAuditsGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdHistoryEmployeeRecordIdAuditsGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_HISTORY_AUDITS, body],
    queryFn: () =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdHistoryEmployeeRecordIdAuditsGet(
        body
      ),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(body.pkEmployeeId),
  });
};

export const useEmployeeShiftsAuditsGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdShiftsAuditsGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_WORK_PATTERN_SHIFTS_AUDITS, body],
    queryFn: () =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdShiftsAuditsGet(body),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(body.pkEmployeeId),
  });
};

export const useEmployeeAccrualGroupsGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdAccrualGroupsGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_ACCRUAL_GROUPS, body],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdAccrualGroupsGet(body)
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: Boolean(body.pkEmployeeId),
  });
};

export const useEmployeeHolidayGroupsGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdHolidayGroupsGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_HOLIDAY_GROUPS, body],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdHolidayGroupsGet(body)
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: Boolean(body.pkEmployeeId),
  });
};

export const useEmployeeBaseScheduleGroupsGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdBaseScheduleGroupsGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_BASE_SCHEDULE_GROUPS, body],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdBaseScheduleGroupsGet(body)
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: Boolean(body.pkEmployeeId),
  });
};

export const useEmployeePayRulesGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdPayRulesGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_PAY_RULES, body],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdPayRulesGet(body)
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: Boolean(body.pkEmployeeId),
  });
};

export const useEmployeePayTypesOptionsListGet = (
  body: Parameters<typeof useEmployeePayTypesWithOptionsGet>[0] & {
    labelKey?: keyof PayTypeEntityResponse;
  }
) => {
  const { labelKey, ...rest } = body;
  const { data = [], isSuccess } = useEmployeePayTypesWithOptionsGet(rest);
  const options = useMemo(() => {
    const key = labelKey || 'objectDescription';
    return data?.map((item) => ({
      id: item.pkPayTypeId!,
      label: item[key],
      value: toNumber(item.pkPayTypeId),
      inActive: item.inactive,
    }));
  }, [data, labelKey]);
  const optionsByIdMap = useMemo(() => keyBy(options, 'id'), [options]);
  const labelsByIdMap = useMemo(() => optionsToLabelMap(options), [options]);
  // Also return raw pay types data for balance checking (includes payTypeID string code)
  const payTypesData = data;
  return { options, optionsByIdMap, labelsByIdMap, isSuccess, payTypesData };
};

export const useEmployeeClockInfoAuditsGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdClockInfoClockIndexAuditsGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_AUDITS, body],
    queryFn: () =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdClockInfoClockIndexAuditsGet(
        body
      ),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(body.pkEmployeeId),
  });
};

export const useEmployeeMiscGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdMiscGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_MISC, body],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdMiscGet(body)
        .then((response) => response.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(body.pkEmployeeId),
  });
};

export const useEmployeeWorkPatternAuditsGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdWorkPatternWorkPatternIdAuditsGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_WORK_PATTERN_AUDITS, body],
    queryFn: () =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdWorkPatternWorkPatternIdAuditsGet(
        body
      ),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(body.pkEmployeeId),
  });
};

export const useEmployeeStatusAuditsGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdStatusEmployeeStatusIdAuditsGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_HISTORY_AUDITS, body],
    queryFn: () =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdStatusEmployeeStatusIdAuditsGet(
        body
      ),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(body.pkEmployeeId),
  });
};

export const useEmployeeLicenseAuditsGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdEmployeeLicenseAuditsGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_LICENSE_AUDIT, body],
    queryFn: () =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdEmployeeLicenseAuditsGet(
        body
      ),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(body.pkEmployeeId),
  });
};

export const useEmployeePayRatesAuditsGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdPayRatePayRateIdAuditsGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_PAY_RATES_AUDITS, body],
    queryFn: () =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdPayRatePayRateIdAuditsGet(
        body
      ),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(body.pkEmployeeId),
  });
};

export const useEmployeeHealthAuditsGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdHealthAuditsGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_HEALTH_AUDITS, body],
    queryFn: () =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdHealthAuditsGet(body),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(body.pkEmployeeId),
  });
};

export const useEmployeeMiscAuditsGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdMiscAuditsGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_MISC_AUDIT, body],
    queryFn: () =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdMiscAuditsGet(body),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(body.pkEmployeeId),
  });
};

export const useEmployeeAuditsGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdAuditsGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_AUDITS, body],
    queryFn: () => api.employeeV2Api.apiV2EmployeesPkEmployeeIdAuditsGet(body),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(body.pkEmployeeId),
  });
};

export const useEmployeeContactInfoAuditsGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdContactInfoAuditsGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_CONTACT_INFO_AUDIT, body],
    queryFn: () =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdContactInfoAuditsGet(body),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(body.pkEmployeeId),
  });
};

export const useEmployeeAuthorizationAuditsGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdAuthorizationAuditsGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_AUTHORIZATION_AUDIT, body],
    queryFn: () =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdAuthorizationAuditsGet(body),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(body.pkEmployeeId),
  });
};

export const useEmployeeInServiceAuditsGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdEmploymentEmployeeInServicesEmployeeInServiceIdAuditsGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_IN_SERVICE_AUDIT, body],
    queryFn: () =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdEmploymentEmployeeInServicesEmployeeInServiceIdAuditsGet(
        body
      ),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(body.pkEmployeeId) && Boolean(body.employeeInServiceId),
  });
};

export const useEmployeeJournalAuditsGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdJournalsPkEmployeeJournalIdAuditsGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_JOURNAL_AUDIT, body],
    queryFn: () =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdJournalsPkEmployeeJournalIdAuditsGet(
        body
      ),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(body.pkEmployeeId) && Boolean(body.pkEmployeeJournalId),
  });
};

export const useEmployeePayRatesSettingsGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdPayRatesSettingsGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_PAY_RATES_SETTINGS, body],
    queryFn: async () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdPayRatesSettingsGet(body)
        .then((response) => response.data),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(body.pkEmployeeId),
  });
};

export type PayRatesSettingResponse = NonNullable<
  ReturnType<typeof useEmployeePayRatesSettingsGet>['data']
>;

export const useEmployeePayRatesWithPermissionsGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdPayRatesGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_PAY_RATES, body],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdPayRatesGet(body)
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(body.pkEmployeeId),
  });
};

export type EmployeePayRatesResponseType = NonNullable<
  UnwrapArray<ReturnType<typeof useEmployeePayRatesWithPermissionsGet>['data']>
>;

export type EmployeePayRatesItem = NonNullable<
  UnwrapArray<EmployeePayRatesResponseType['payRates']>
>;

export const useEmployeeLicensesGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdEmploymentLicenseGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_LICENSES, body],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdEmploymentLicenseGet(body)
        .then((response) => response.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(body.pkEmployeeId),
  });
};

export const useI9DocumentsGetWithMutation = () => {
  const api = useApi();
  return useMutation(
    (
      body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdEmploymentI9DocumentsGetRequest
    ) =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdEmploymentI9DocumentsGet(body)
        .then((response) => response.data ?? {})
  );
};

export type EmployeeI9DocumentResponse = UnwrapArray<
  NonNullable<ReturnType<typeof useI9DocumentsGetWithMutation>['data']>
>;

export const useEmployeeHealthGetWithMutation = () => {
  const api = useApi();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdEmploymentHealthGetRequest) =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdEmploymentHealthGet(body)
        .then((response) => response.data ?? {})
  );
};

export type EmployeeHealth = UnwrapArray<
  NonNullable<ReturnType<typeof useEmployeeHealthGetWithMutation>['data']>
>;

export const useEmployeeGeneralGetWithMutation = () => {
  const api = useApi();
  return useMutation(
    async (
      body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdEmploymentGeneralGetRequest
    ) => {
      return api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdEmploymentGeneralGet(body)
        .then((response) => response.data ?? {});
    }
  );
};

export type EmployeeGeneral = UnwrapArray<
  NonNullable<ReturnType<typeof useEmployeeGeneralGetWithMutation>['data']>
>;

export const useEmployeeShiftsGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdShiftsGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.SHIFT_HISTORY, body],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdShiftsGet(body)
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export type EmployeeShift = UnwrapArray<
  NonNullable<ReturnType<typeof useEmployeeShiftsGet>['data']>
>;

export const useGetWorkPatternConfiguration = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdWorkPatternConfigurationGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.WORK_PATTERN_CONFIGURATION, body],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdWorkPatternConfigurationGet(body)
        .then((response) => response?.data),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: Boolean(body.pkEmployeeId),
  });
};

export const useEmployeeWorkPatternsGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdWorkPatternsGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.WORK_PATTERN, body],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdWorkPatternsGet(body)
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useEmployeeContactInfoGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdContactInfoGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.CONTACT_INFO, body],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdContactInfoGet(body)
        .then((response) => response.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(body.pkEmployeeId),
  });
};

export const useEmployeeContactInfoGetWithMutation = () => {
  const api = useApi();
  return useMutation(
    async (
      body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdContactInfoGetRequest
    ) => {
      return api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdContactInfoGet(body)
        .then((response) => response.data ?? {});
    }
  );
};

export type EmployeeContactInfoResponseType = ReturnType<
  typeof useEmployeeContactInfoGetWithMutation
>['data'];

export const useEmployeeJournalsGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdJournalsGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_JOURNALS, body],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdJournalsGet(body)
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useEmployeeHistoryGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdHistoriesGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_HISTORY, body],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdHistoriesGet(body)
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useEmployeeHistoryMinEffectiveDateGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdHistoriesMinEffectiveDateGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_HISTORY_MIN_EFFECTIVE_DATE, body],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdHistoriesMinEffectiveDateGet(body)
        .then((response) => response.data ?? null),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(body.pkEmployeeId),
  });
};

export const useEmployeeStatusesGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdStatusesGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_STATUSES, body],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdStatusesGet(body)
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

type EmployeeStatusResponse = UnwrapArray<
  NonNullable<ReturnType<typeof useEmployeeStatusesGet>['data']>
>;

export type EmployeeStatusResponseType = UnwrapArray<
  NonNullable<EmployeeStatusResponse['employeeStatuses']>
>;

export const useEmployeeMiscGetWithMutation = () => {
  const api = useApi();
  return useMutation(
    async (body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdMiscGetRequest) => {
      return api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdMiscGet(body)
        .then((response) => response.data ?? {});
    }
  );
};

export type EmployeeMiscResponseType = UnwrapArray<
  NonNullable<ReturnType<typeof useEmployeeMiscGetWithMutation>['data']>
>;

export const useLoggedInEmployeeUserGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.LOGGED_IN_EMPLOYEE_USER],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesMeGet()
        .then((response) => response.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useLicenseStatus = () => {
  const { data } = useLoggedInEmployeeUserGet();
  return data?.license ?? {};
};

export const useEmployeeSalariedHoursGet = (employeeId: number) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_SALARIED_HOURS, { employeeId }],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdSalariedHoursGet({
          pkEmployeeId: employeeId,
        } as EmployeeV2ApiApiV2EmployeesPkEmployeeIdSalariedHoursGetRequest)
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useEmployeeAuthorizationGet = (employeeId: number) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_AUTHORIZATION_CONFIG, employeeId],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdAuthorizationGet({
          pkEmployeeId: employeeId,
        } as EmployeeV2ApiApiV2EmployeesPkEmployeeIdAuthorizationGetRequest)
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(employeeId),
  });
};

export const useEmployeeAuthorizationRolesGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdAuthorizationRolesGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_AUTHORIZATION_CONFIG, body],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdAuthorizationRolesGet(body)
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(body.pkEmployeeId),
  });
};

export const useEmployeeInServiceCategoryListGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdEmploymentInServiceListServiceCategoryGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_IN_SERVICE_LIST, body],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdEmploymentInServiceListServiceCategoryGet(
          body
        )
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(body.pkEmployeeId),
  });
};

export const useEmployeeClockInfoGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdClockInfoGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.FACILITY_TIME_CLOCKS, body],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdClockInfoGet(body)
        .then((response) => response.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(body.pkEmployeeId),
  });
};

export const useEmployeeClockInfoLaborLevelsGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdClockInfoLaborLevelsGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_CLOCK_INFO_LABOR_LEVELS, body],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdClockInfoLaborLevelsGet(body)
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(body.pkEmployeeId),
  });
};

export const useEmployeeClockInfoListGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdClockInfoListGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_CLOCK_INFO_LIST, body],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdClockInfoListGet(body)
        .then((response) => response.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(body.pkEmployeeId),
  });
};

export type EmployeeClockListResponse = NonNullable<
  ReturnType<typeof useEmployeeClockInfoListGet>['data']
>;

export type EmployeeClockListItemResponse = UnwrapArray<
  NonNullable<EmployeeClockListResponse['clockIds']>
>;

export const useEmployeesPost = () => {
  const api = useApi();
  return useMutation((body: EmployeeV2ApiApiV2EmployeesPostRequest) =>
    api.employeeV2Api
      .apiV2EmployeesPost(body)
      .then((response) => response.data ?? [])
  );
};

export const useUploadProfilePicture = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifyError } = useSnackBar();
  return useMutation(
    async (
      body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdProfilePicturePostRequest
    ) =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdProfilePicturePost(body)
        .then((response) => response.data),
    {
      onSuccess: (_data, variables) => {
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EmployeeHeader])
          .catch(noop);
        queryClient
          .invalidateQueries([UserCacheKeys.RECENTLY_VIEWED_EMPLOYEES])
          .catch(noop);
      },
      onError: (error) => {
        notifyError(getApiErrorMessage(error));
      },
    }
  );
};

export const useUpdateEmployee = () => {
  const api = useApi();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdPutRequest) => {
      return api.employeeV2Api.apiV2EmployeesPkEmployeeIdPut(body);
    }
  );
};

export const useUpdateEmployeeId = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdEmployeeIdPutRequest) =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdEmployeeIdPut(body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([EmployeeCacheKeys.EMPLOYEE]).catch(noop);
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EmployeeHeader])
          .catch(noop);
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEES])
          .catch(noop);
      },
    }
  );
};

export const useUpdateEmployeePayRule = () => {
  const { notifyError } = useSnackBar();
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdPayRulePutRequest) =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdPayRulePut(body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([EmployeeCacheKeys.EMPLOYEE]).catch(noop);
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EmployeeHeader])
          .catch(noop);
      },
      onError: (error) => {
        notifyError(getApiErrorMessage(error));
      },
    }
  );
};

export const useUpdateEmployeeAccrualGroup = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();
  const queryClient = useQueryClient();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdAccrualGroupPutRequest) =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdAccrualGroupPut(body),
    {
      onError: (error) => {
        notifyError(getApiErrorMessage(error));
      },
      onSuccess: () => {
        queryClient.invalidateQueries([EmployeeCacheKeys.EMPLOYEE]).catch(noop);
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EmployeeHeader])
          .catch(noop);
      },
    }
  );
};

export const useUpdateEmployeeBaseSchedule = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();
  const queryClient = useQueryClient();
  return useMutation(
    (
      body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdBaseScheduleGroupPutRequest
    ) => api.employeeV2Api.apiV2EmployeesPkEmployeeIdBaseScheduleGroupPut(body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([EmployeeCacheKeys.EMPLOYEE]).catch(noop);
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EmployeeHeader])
          .catch(noop);
      },
      onError: (error) => {
        notifyError(getApiErrorMessage(error));
      },
    }
  );
};

export const useUpdateEmployeeHolidayGroup = () => {
  const { notifyError } = useSnackBar();
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdHolidayGroupPutRequest) =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdHolidayGroupPut(body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([EmployeeCacheKeys.EMPLOYEE]).catch(noop);
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EmployeeHeader])
          .catch(noop);
      },
      onError: (error) => {
        notifyError(getApiErrorMessage(error));
      },
    }
  );
};

export const useUpdateLaborLevelsGroup = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdLaborLevelsPutRequest) =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdLaborLevelsPut(body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([EmployeeCacheKeys.EMPLOYEE]).catch(noop);
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EmployeeHeader])
          .catch(noop);
      },
    }
  );
};

export const useEmployeeClockInfoGetWithMutation = () => {
  const api = useApi();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdClockInfoGetRequest) =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdClockInfoGet(body)
        .then((response) => response?.data)
  );
};

export type EmployeeClockResponse = NonNullable<
  ReturnType<typeof useEmployeeClockInfoGetWithMutation>['data']
>;

export const useEmployeeFacilityTimeClocksGet = (pkEmployeeId: number) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.FACILITY_TIME_CLOCKS, { pkEmployeeId }],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdClockInfoFacilityTimeClocksGet({
          pkEmployeeId,
        })
        .then((response) => response?.data),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(pkEmployeeId),
  });
};

export type TimeClockItemResponseType = UnwrapArray<
  NonNullable<ReturnType<typeof useEmployeeFacilityTimeClocksGet>['data']>
>;

export const useDeleteEmployeeStatus = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (
      body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdStatusesPkEmployeeStatusIdDeleteRequest
    ) =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdStatusesPkEmployeeStatusIdDelete(
        body
      ),
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_STATUSES])
          .catch(noop);
      },
    }
  );
};

export const useCreateStatusType = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdStatusesPostRequest) =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdStatusesPost(body),
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_STATUSES])
          .catch(noop);
      },
    }
  );
};

export const useUpdateStatusType = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (
      body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdStatusesPkEmployeeStatusIdPutRequest
    ) =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdStatusesPkEmployeeStatusIdPut(
        body
      ),
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_STATUSES])
          .catch(noop);
      },
    }
  );
};

export const useUpdateEmployeeClockInfo = () => {
  const { notifyError } = useSnackBar();
  const api = useApi();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdClockInfoPutRequest) =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdClockInfoPut(body),
    {
      onError: async (error) => {
        notifyError(getApiErrorMessage(error));
      },
    }
  );
};

export const useCreateEmployeeClockInfo = () => {
  const { notifyError } = useSnackBar();
  const api = useApi();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdClockInfoPostRequest) =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdClockInfoPost(body),
    {
      onError: async (error) => {
        notifyError(getApiErrorMessage(error));
      },
    }
  );
};

export const useAssignEmployeeClock = () => {
  const { notifyError } = useSnackBar();
  const api = useApi();
  return useMutation(
    (
      body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdClockInfoAssignmentsPatchRequest
    ) =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdClockInfoAssignmentsPatch(
        body
      ),
    {
      onError: async (error) => {
        notifyError(getApiErrorMessage(error));
      },
    }
  );
};

export const useAssignAllClock = () => {
  const { notifyError } = useSnackBar();
  const api = useApi();
  return useMutation(
    (
      body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdClockInfoAssignmentAllPatchRequest
    ) =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdClockInfoAssignmentAllPatch(
        body
      ),
    {
      onError: async (error) => {
        notifyError(getApiErrorMessage(error));
      },
    }
  );
};

export const useDeleteEmployeeHistory = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (
      body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdHistoryPkHistoryIdDeleteRequest
    ) =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdHistoryPkHistoryIdDelete(
        body
      ),
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_HISTORY])
          .catch(noop);
      },
    }
  );
};

export const useDeleteEmployeeJournal = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (
      body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdJournalsPkEmployeeJournalIdDeleteRequest
    ) =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdJournalsPkEmployeeJournalIdDelete(
        body
      ),
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_JOURNALS])
          .catch(noop);
      },
    }
  );
};

export const useUpdateEmployeeJournal = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdJournalsPutRequest) =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdJournalsPut(body),
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_JOURNALS])
          .catch(noop);
      },
    }
  );
};

export const useAddEmployeeJournal = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdJournalsPostRequest) =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdJournalsPost(body),
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_JOURNALS])
          .catch(noop);
      },
    }
  );
};

export const useUpdateContactInfo = () => {
  const api = useApi();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdContactInfoPutRequest) =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdContactInfoPut(body)
  );
};

export const useCreateWorkPattern = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdWorkPatternsPostRequest) =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdWorkPatternsPost(body),
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries([EmployeeCacheKeys.WORK_PATTERN])
          .catch(noop);
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EmployeeHeader])
          .catch(noop);
      },
    }
  );
};

export const useDeleteWorkPattern = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesWorkPatternDeleteRequest) => {
      return api.employeeV2Api.apiV2EmployeesWorkPatternDelete(body);
    },
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries([EmployeeCacheKeys.WORK_PATTERN])
          .catch(noop);
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EmployeeHeader])
          .catch(noop);
      },
    }
  );
};

export const useCreateEmployeeShift = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdShiftsPostRequest) => {
      return api.employeeV2Api.apiV2EmployeesPkEmployeeIdShiftsPost(body);
    },
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries([EmployeeCacheKeys.SHIFT_HISTORY])
          .catch(noop);
      },
    }
  );
};

export const useUpdateEmployeeShift = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdShiftsPutRequest) => {
      return api.employeeV2Api.apiV2EmployeesPkEmployeeIdShiftsPut(body);
    },
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries([EmployeeCacheKeys.SHIFT_HISTORY])
          .catch(printErrorToConsole);
      },
    }
  );
};

export const useDeleteEmployeeShift = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdShiftsDeleteRequest) => {
      return api.employeeV2Api.apiV2EmployeesPkEmployeeIdShiftsDelete(body);
    },
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries([EmployeeCacheKeys.SHIFT_HISTORY])
          .catch(noop);
      },
    }
  );
};

export const useEmployeeShiftAdjustments = () => {
  const api = useApi();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdShiftsPostRequest) => {
      return api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdShiftsPost(body)
        .then((response) => response.data);
    }
  );
};

export const useUpdateEmployeeMisc = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdMiscPutRequest) =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdMiscPut(body),
    {
      onError: async (error) => {
        notifyError(getApiErrorMessage(error));
      },
    }
  );
};

export const useUpdateEmployeeGeneral = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesEmploymentGeneralPutRequest) => {
      return api.employeeV2Api.apiV2EmployeesEmploymentGeneralPut(body);
    },
    {
      onError: async (error) => {
        notifyError(getApiErrorMessage(error));
      },
    }
  );
};

export const useUpdateEmployeeHealth = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesEmploymentHealthPutRequest) => {
      return api.employeeV2Api.apiV2EmployeesEmploymentHealthPut(body);
    },
    {
      onError: async (error) => {
        notifyError(getApiErrorMessage(error));
      },
    }
  );
};

export const useUpdateI9Documents = () => {
  const api = useApi();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesEmploymentI9DocumentsPutRequest) => {
      return api.employeeV2Api.apiV2EmployeesEmploymentI9DocumentsPut(body);
    }
  );
};

export const useUpdateEmployeeLicenses = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesEmploymentEmployeeLicensePutRequest) => {
      return api.employeeV2Api.apiV2EmployeesEmploymentEmployeeLicensePut(body);
    },
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_LICENSES])
          .catch(() => {});
      },
    }
  );
};

export const useUpdateEmployeePayRate = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (
      body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdPayRatePayRateIdPutRequest
    ) => {
      return api.employeeV2Api.apiV2EmployeesPkEmployeeIdPayRatePayRateIdPut(
        body
      );
    },
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_PAY_RATES])
          .catch(noop);
      },
    }
  );
};

export const useCreateEmployeePayRate = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifyError } = useSnackBar();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdPayRatePostRequest) => {
      return api.employeeV2Api.apiV2EmployeesPkEmployeeIdPayRatePost(body);
    },
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_PAY_RATES])
          .catch(noop);
      },
      onError: async (error) => {
        notifyError(getApiErrorMessage(error));
      },
    }
  );
};

export const useDeleteEmployeePayRate = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (
      body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdPayRatePayRateIdDeleteRequest
    ) => {
      return api.employeeV2Api.apiV2EmployeesPkEmployeeIdPayRatePayRateIdDelete(
        body
      );
    },
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_PAY_RATES])
          .catch(noop);
      },
    }
  );
};

export const useUpdateEmployeeSalariedHours = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdSalariedHoursPutRequest) =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdSalariedHoursPut(body),
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_SALARIED_HOURS])
          .catch(noop);
      },
    }
  );
};

export const useAddEmployeeInServices = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();
  const queryClient = useQueryClient();
  return useMutation(
    (
      body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdEmploymentInServicePostRequest
    ) => {
      return api.employeeV2Api.apiV2EmployeesPkEmployeeIdEmploymentInServicePost(
        body
      );
    },
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_IN_SERVICE_LIST])
          .catch(noop);
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_IN_SERVICE])
          .catch(noop);
      },
      onError: async (error) => {
        const message =
          get(error, 'response.data.message', '') || get(error, 'message', '');
        notifyError(message);
      },
    }
  );
};

export const useEmployeeInServiceGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdEmploymentInServiceListServiceCategoryGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_IN_SERVICE, body],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdEmploymentInServiceListServiceCategoryGet(
          body
        )
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useEmployeeInServiceListGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdEmploymentEmployeeInServicesGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_IN_SERVICE_LIST, body],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdEmploymentEmployeeInServicesGet(body)
        .then((response) => response.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(body.pkEmployeeId),
  });
};

export type EmployeeInServiceListItemResponse = NonNullable<
  UnwrapArray<ReturnType<typeof useEmployeeInServiceListGet>['data']>
>;

export const useRemoveEmployeeInService = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (
      body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdEmploymentEmployeeInServiceInServiceIdDeleteRequest
    ) => {
      return api.employeeV2Api.apiV2EmployeesPkEmployeeIdEmploymentEmployeeInServiceInServiceIdDelete(
        body
      );
    },
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_IN_SERVICE_LIST])
          .catch(noop);
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_IN_SERVICE])
          .catch(noop);
      },
    }
  );
};

export const useEmployeeAuthorizationRole = (employeeId: number) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_AUTHORIZATION, { employeeId }],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdAuthorizationRolesGet({
          pkEmployeeId: employeeId,
        } as EmployeeV2ApiApiV2EmployeesPkEmployeeIdAuthorizationRolesGetRequest)
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(employeeId),
  });
};

export const useEmployeeAuthorizationRoleOptions = (employeeId: number) => {
  const { data: roles = [] } = useEmployeeAuthorizationRole(employeeId);
  const options = useMemo(
    () =>
      roles.map((item) => ({
        id: item,
        label: item,
        value: item,
      })),
    [roles]
  );
  return { options };
};

export const useUpdateEmployeeAuthorization = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdAuthorizationPutRequest) => {
      return api.employeeV2Api.apiV2EmployeesPkEmployeeIdAuthorizationPut(body);
    },
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_AUTHORIZATION_CONFIG])
          .catch(noop);
      },
    }
  );
};

export const useEnableEmployeeAuthorization = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdAuthorizationPostRequest) => {
      return api.employeeV2Api.apiV2EmployeesPkEmployeeIdAuthorizationPost(
        body
      );
    },
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_AUTHORIZATION_CONFIG])
          .catch(noop);
      },
    }
  );
};

export const useDisableEmployeeAuthorization = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (
      body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdAuthorizationDeleteRequest
    ) => {
      return api.employeeV2Api.apiV2EmployeesPkEmployeeIdAuthorizationDelete(
        body
      );
    },
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_AUTHORIZATION_CONFIG])
          .catch(noop);
      },
    }
  );
};

export const useCloneEmployeeAuthorization = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (
      body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdAuthorizationLaborLevelAuthorizationLaborLevelIdClonePostRequest
    ) => {
      return api.employeeV2Api.apiV2EmployeesPkEmployeeIdAuthorizationLaborLevelAuthorizationLaborLevelIdClonePost(
        body
      );
    },
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_AUTHORIZATION_CONFIG])
          .catch(noop);
      },
    }
  );
};

export const useRemoveEmployeeLaborLevel = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (
      body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdAuthorizationLaborLevelLaborLevelIdDeleteRequest
    ) => {
      return api.employeeV2Api.apiV2EmployeesPkEmployeeIdAuthorizationLaborLevelLaborLevelIdDelete(
        body
      );
    },
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_AUTHORIZATION_CONFIG])
          .catch(noop);
      },
    }
  );
};

export const useUpdateEmployeeLaborLevel = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (
      body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdAuthorizationLaborLevelLaborLevelIdPutRequest
    ) => {
      return api.employeeV2Api.apiV2EmployeesPkEmployeeIdAuthorizationLaborLevelLaborLevelIdPut(
        body
      );
    },
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_AUTHORIZATION_CONFIG])
          .catch(noop);
      },
    }
  );
};

export const useAddEmployeeLaborLevel = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifyError } = useSnackBar();
  return useMutation(
    (
      body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdAuthorizationLaborLevelPostRequest
    ) => {
      return api.employeeV2Api.apiV2EmployeesPkEmployeeIdAuthorizationLaborLevelPost(
        body
      );
    },
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_AUTHORIZATION_CONFIG])
          .catch(noop);
      },
      onError: async (error) => {
        notifyError(getApiErrorMessage(error));
      },
    }
  );
};

export const useCreateEmployee = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesCreatePostRequest) => {
      return api.employeeV2Api.apiV2EmployeesCreatePost(body);
    },
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_CREATE])
          .catch(noop);
      },
    }
  );
};

export const useCheckDuplicateEmployeeId = () => {
  const api = useApi();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesCheckDuplicateIdGetRequest) =>
      api.employeeV2Api
        .apiV2EmployeesCheckDuplicateIdGet(body)
        .then((response) => response.data ?? {})
  );
};

export const useCheckDuplicateClockId = () => {
  const api = useApi();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesCheckDuplicateClockIdGetRequest) =>
      api.employeeV2Api
        .apiV2EmployeesCheckDuplicateClockIdGet(body)
        .then((response) => response.data ?? {})
  );
};

export const useGetNextAvailableEmployeeId = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (body: EmployeeV2ApiApiV2EmployeesNextAvailableIdGetRequest) =>
      api.employeeV2Api.apiV2EmployeesNextAvailableIdGet(body),
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_GET_ID])
          .catch(noop);
      },
    }
  );
};

export const useUpdateEmployeeClockInfoLaborLevelsPut = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (
      body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdClockInfoLaborLevelsPutRequest
    ) =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdClockInfoLaborLevelsPut(body),
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries([
            EmployeeCacheKeys.EMPLOYEE_CLOCK_INFO_LABOR_LEVELS,
          ])
          .catch(noop);
      },
    }
  );
};

export const useEmployeeLaborLevelsCheck = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (
      body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdLaborLevelsCheckPatchRequest
    ) =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdLaborLevelsCheckPatch(body),
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_CHECK_LABOR_LEVELS])
          .catch(noop);
      },
    }
  );
};

export const usePayPeriodGroupGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdPayPeriodGroupGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_PERIOD_GROUP, body],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdPayPeriodGroupGet(body)
        .then((response) => response?.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useWorkingPayPeriodPatch = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  return useMutation(
    (
      body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdWorkingPayPeriodWorkingPayPeriodIdPatchRequest
    ) =>
      api.employeeV2Api.apiV2EmployeesPkEmployeeIdWorkingPayPeriodWorkingPayPeriodIdPatch(
        body
      ),
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries([TimeCardCacheKeys.PAY_PERIOD_APPROVAL])
          .catch(noop);
      },
    }
  );
};

export const useEffectiveDateConfigurationGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdEffectiveDateConfigurationGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_EFFECTIVE_DATE_CONFIG, body],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdEffectiveDateConfigurationGet(body)
        .then((response) => response?.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useEmployeeHistoryTypesGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdHistoryTypesGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_HISTORY_TYPES, body],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdHistoryTypesGet(body)
        .then((response) => response.data),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useEmployeeHistoryTypesOptionsGet = ({
  pkEmployeeId,
}: EmployeeV2ApiApiV2EmployeesPkEmployeeIdHistoryTypesGetRequest) => {
  const {
    data = [],
    isLoading,
    isSuccess,
  } = useEmployeeHistoryTypesGet({
    pkEmployeeId,
  });
  const options = useMemo(
    () =>
      map(data, (item) => ({
        id: toNumber(item.historyType),
        label: keyToLabel(item.name ?? ''),
        value: toNumber(item.historyType),
      })),
    [data]
  );
  return { options, isLoading, isSuccess };
};

const useEmployeeStatusTypeGet = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdEmployeeStatusTypesGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [EntityListCacheKeys.EMPLOYEE_STATUS_TYPE, body.pkEmployeeId],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdEmployeeStatusTypesGet(body)
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: body.pkEmployeeId > 0,
  });
};

export const useEmployeeStatusTypeOptions = (
  body: EmployeeV2ApiApiV2EmployeesPkEmployeeIdEmployeeStatusTypesGetRequest
) => {
  const { data, isLoading, isSuccess } = useEmployeeStatusTypeGet(body);
  const options = useMemo(
    () =>
      map(data, (item) => {
        return {
          id: item.pkEmployeeStatusTypeId as number,
          label: item.employeeStatusTypeId ?? '',
          value: item.pkEmployeeStatusTypeId as number,
          meta: item,
        };
      }),
    [data]
  );

  return { options, isLoading, isSuccess };
};

export const useEmployeeLicenseTypeList = (employeeId: number) => {
  const api = useApi();
  return useQuery({
    queryKey: [EntityListCacheKeys.EMPLOYEE_LICENSE_TYPE],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdLicenseTypesGet({ pkEmployeeId: employeeId })
        .then((response) => response.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
  });
};
